import { Controller } from "@hotwired/stimulus"

// Import portfolio configuration modal (Step 20).
// Multi-step inside one modal:
//   1. idle       — drop zone
//   2. loading    — parsing + /preview fetch
//   3. preview    — per-category diff + conflict strategy radios
//   4. applying   — /import fetch
//
// Payload shape between JS and the controller:
//   preview POST: { bundle: "<raw JSON string>", conflict_strategies: {...}, role_map: {...} }
//   import  POST: same, minus dry_run.
export default class extends Controller {
  static targets = [
    "dropScreen", "loadingScreen", "loadingLabel", "previewScreen", "errors",
    "dropZone", "fileInput",
    "roleMapScreen", "roleMapList", "roleMapRowTemplate", "roleMapContinue",
    "sourceName", "sourceMeta", "noOpBanner",
    "themeStatus", "thresholdsStatus", "presetsStatus", "alertsStatus", "reportsStatus",
    "applyButton"
  ]
  static values = { previewUrl: String, importUrl: String, availableRoles: Array }

  connect() {
    this._bundleJson = null
    this._strategies = this._defaultStrategies()
    this._roleMap = {}
    this._dragDepth = 0
    // The generic modal controller toggles a `hidden` class on this element
    // to open/close. When the modal is re-opened after a previous session,
    // reset state so the user doesn't see a stale preview.
    this._observer = new MutationObserver(() => {
      if (!this.element.classList.contains("hidden")) this._onModalOpen()
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["class"] })
  }

  disconnect() {
    this._observer?.disconnect()
  }

  _onModalOpen() {
    // Fresh state on every open. A previous preview would otherwise be
    // stale against the current tenant's configuration.
    this._bundleJson = null
    this._strategies = this._defaultStrategies()
    this._roleMap = {}
    this._dragDepth = 0
    this.fileInputTarget.value = ""
    this._resetStrategyUI()
    this._showDrop()
    this.errorsTarget.classList.add("hidden")
  }

  _defaultStrategies() {
    return {
      theme: "replace",
      rag_thresholds: "replace",
      filter_presets: "replace",
      alert_rules: "replace",
      scheduled_reports: "replace"
    }
  }

  // ── File drop ─────────────────────────────────────────────────────

  // Depth counter pattern: dragenter/dragleave fire for each child element
  // the cursor crosses, so a naive "toggle on enter/leave" flickers. We
  // count nested enters and only clear the highlight when the count
  // returns to zero.
  dragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this._dragDepth += 1
    this.dropZoneTarget.classList.add("border-[var(--portfolio-primary)]", "bg-blue-50")
    this.dropZoneTarget.classList.remove("border-gray-300")
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "copy"
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this._dragDepth -= 1
    if (this._dragDepth <= 0) {
      this._dragDepth = 0
      this._clearDragHighlight()
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this._dragDepth = 0
    this._clearDragHighlight()
    const file = Array.from(event.dataTransfer?.files || [])[0]
    if (file) this._handleFile(file)
  }

  fileChosen(event) {
    const file = event.target.files?.[0]
    if (file) this._handleFile(file)
  }

  _clearDragHighlight() {
    this.dropZoneTarget.classList.remove("border-[var(--portfolio-primary)]", "bg-blue-50")
    this.dropZoneTarget.classList.add("border-gray-300")
  }

  async _handleFile(file) {
    // Clear the input so re-selecting the same file after an error still
    // fires a `change` event next time.
    this.fileInputTarget.value = ""

    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      this._renderError("Only .json bundles are accepted.")
      return
    }
    if (file.size > 1024 * 1024) {
      this._renderError("Bundles must be under 1 MB.")
      return
    }
    this._showLoading("Reading bundle…")
    try {
      const text = await file.text()
      JSON.parse(text) // validate upfront; server re-validates too
      this._bundleJson = text
      await this._fetchPreview()
    } catch (err) {
      console.error("Import read failed", err)
      this._renderError("Could not parse JSON from this file.")
      this._showDrop()
    }
  }

  async _fetchPreview() {
    this._showLoading("Computing diff against this tenant…")
    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        body: JSON.stringify({
          bundle: this._bundleJson,
          conflict_strategies: this._strategies,
          role_map: this._roleMap
        })
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        const msg = Array.isArray(json.errors) ? json.errors.join("; ") : `HTTP ${res.status}`
        this._renderError(msg)
        this._showDrop()
        return
      }
      this._lastPreview = json
      // If the bundle references roles we haven't mapped yet, route to the
      // role-mapping step before showing the diff. Once the user submits a
      // mapping, _fetchPreview runs again and unknown_roles is empty.
      const unknown = Array.isArray(json.unknown_roles) ? json.unknown_roles : []
      if (unknown.length > 0) {
        this._showRoleMap(unknown)
      } else {
        this._renderPreview(json)
      }
    } catch (err) {
      console.error("Preview failed", err)
      this._renderError("Could not compute the preview.")
      this._showDrop()
    }
  }

  async apply(event) {
    event?.preventDefault()
    this.applyButtonTarget.disabled = true
    this._showLoading("Applying import…")
    try {
      const res = await fetch(this.importUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf()
        },
        body: JSON.stringify({
          bundle: this._bundleJson,
          conflict_strategies: this._strategies,
          role_map: this._roleMap
        })
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.success === false) {
        const msg = Array.isArray(json.errors) ? json.errors.join("; ") : `HTTP ${res.status}`
        this._renderError(msg)
        this.applyButtonTarget.disabled = false
        this._showPreview()
        return
      }
      const counts = this._summarizeCounts(json.counts)
      this._toast(json.no_op ? "No changes — bundle matches current configuration." : `Import applied (${counts}).`, "notice")
      this._close()
      // Preset pills, alert rule list, and scheduled report list live in
      // separate modals/partials — a full reload is the cleanest way to
      // surface the new state without re-building five DOM contracts here.
      setTimeout(() => window.location.reload(), 400)
    } catch (err) {
      console.error("Import failed", err)
      this._renderError("Could not apply the import.")
      this.applyButtonTarget.disabled = false
      this._showPreview()
    }
  }

  strategyChanged(event) {
    const section = event.currentTarget.dataset.section
    const value = event.currentTarget.value
    if (!section || !value) return
    this._strategies[section] = value
    this._fetchPreview()
  }

  reset(event) {
    event?.preventDefault()
    this._bundleJson = null
    this._strategies = this._defaultStrategies()
    this._roleMap = {}
    this.fileInputTarget.value = ""
    this._resetStrategyUI()
    this._showDrop()
    this.errorsTarget.classList.add("hidden")
  }

  // ── Render helpers ────────────────────────────────────────────────

  _renderPreview(payload) {
    this.errorsTarget.classList.add("hidden")
    this._showPreview()
    this.applyButtonTarget.classList.remove("hidden")
    this.applyButtonTarget.disabled = false

    const from = payload.exported_from || {}
    this.sourceNameTarget.textContent = from.company_name || "Unknown source"
    const bits = []
    if (from.company_slug) bits.push(from.company_slug)
    if (payload.exported_at) bits.push(new Date(payload.exported_at).toLocaleString())
    if (payload.schema_version) bits.push(`Schema v${payload.schema_version}`)
    this.sourceMetaTarget.textContent = bits.join(" · ")

    this.noOpBannerTarget.classList.toggle("hidden", !payload.no_op)

    const plan = payload.plan || {}
    this.themeStatusTarget.textContent = this._describeScalarPlan(plan.theme)
    this.thresholdsStatusTarget.textContent = this._describeScalarPlan(plan.rag_thresholds)
    this.presetsStatusTarget.textContent = this._describeListPlan(plan.filter_presets)
    this.alertsStatusTarget.textContent = this._describeListPlan(plan.alert_rules)
    this.reportsStatusTarget.textContent = this._describeListPlan(plan.scheduled_reports)
  }

  _describeScalarPlan(section) {
    if (!section) return "—"
    const action = section.action
    if (action === "skip") return "No change"
    if (action === "create") return "Will be set"
    if (action === "update") return "Will overwrite current"
    return action || "—"
  }

  _describeListPlan(section) {
    if (!section) return "—"
    const items = section.items || []
    const removed = section.removed || []
    const creates = items.filter((i) => i.action === "create").length
    const updates = items.filter((i) => i.action === "update").length
    const skips   = items.filter((i) => i.action === "skip").length
    const deletes = removed.filter((i) => i.action === "delete").length
    const parts = []
    if (creates) parts.push(`${creates} new`)
    if (updates) parts.push(`${updates} updated`)
    // Under `skip` strategy every item is action=skip — the user is
    // opting out of changes, not leaving items "unchanged". "Ignored" is
    // clearer. Under other strategies, action=skip means "identical to
    // current" → "unchanged" is correct.
    if (skips)   parts.push(`${skips} ${section.strategy === "skip" ? "ignored" : "unchanged"}`)
    if (deletes) parts.push(`${deletes} removed`)
    return parts.length ? parts.join(" · ") : "Nothing"
  }

  _summarizeCounts(counts) {
    const sections = Object.keys(counts || {})
    const total = sections.reduce((n, s) => {
      const bucket = counts[s] || {}
      return n + Object.keys(bucket).reduce((x, k) => k === "skip" ? x : x + bucket[k], 0)
    }, 0)
    return `${total} change${total === 1 ? "" : "s"}`
  }

  _showDrop() {
    this.dropScreenTarget.classList.remove("hidden")
    this.loadingScreenTarget.classList.add("hidden")
    this.previewScreenTarget.classList.add("hidden")
    this.roleMapScreenTarget.classList.add("hidden")
    this.applyButtonTarget.classList.add("hidden")
    this.roleMapContinueTarget.classList.add("hidden")
  }

  _showLoading(label) {
    this.loadingLabelTarget.textContent = label || "Loading…"
    this.dropScreenTarget.classList.add("hidden")
    this.previewScreenTarget.classList.add("hidden")
    this.roleMapScreenTarget.classList.add("hidden")
    this.applyButtonTarget.classList.add("hidden")
    this.roleMapContinueTarget.classList.add("hidden")
    this.loadingScreenTarget.classList.remove("hidden")
    this.errorsTarget.classList.add("hidden")
  }

  _showPreview() {
    this.dropScreenTarget.classList.add("hidden")
    this.loadingScreenTarget.classList.add("hidden")
    this.roleMapScreenTarget.classList.add("hidden")
    this.roleMapContinueTarget.classList.add("hidden")
    this.previewScreenTarget.classList.remove("hidden")
  }

  // ── Role mapping ──────────────────────────────────────────────────

  _showRoleMap(unknownRoles) {
    this.dropScreenTarget.classList.add("hidden")
    this.loadingScreenTarget.classList.add("hidden")
    this.previewScreenTarget.classList.add("hidden")
    this.applyButtonTarget.classList.add("hidden")
    this.roleMapScreenTarget.classList.remove("hidden")
    this.roleMapContinueTarget.classList.remove("hidden")
    this.errorsTarget.classList.add("hidden")

    this.roleMapListTarget.innerHTML = ""
    const available = this.hasAvailableRolesValue ? this.availableRolesValue : []

    unknownRoles.forEach((role) => {
      const row = this.roleMapRowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.querySelector('[data-role="unknown-role"]').textContent = role

      const select = row.querySelector('[data-role="role-map-select"]')
      // The template already has a "Skip" option. Append one <option> per
      // available target role.
      available.forEach((targetRole) => {
        const opt = document.createElement("option")
        opt.value = targetRole
        opt.textContent = targetRole
        select.appendChild(opt)
      })
      select.dataset.unknownRole = role
      this.roleMapListTarget.appendChild(row)
    })
  }

  applyRoleMap(event) {
    event?.preventDefault()
    const map = {}
    this.roleMapListTarget.querySelectorAll('[data-role="role-map-select"]').forEach((select) => {
      const key = select.dataset.unknownRole
      const value = select.value || null
      map[key] = value
    })
    this._roleMap = map
    this._fetchPreview()
  }

  _renderError(msg) {
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
    this.loadingScreenTarget.classList.add("hidden")
  }

  _resetStrategyUI() {
    this.element.querySelectorAll('[data-role="strategy-radio"]').forEach((input) => {
      input.checked = input.value === "replace"
    })
  }

  _close() {
    document.getElementById("portfolio-config-import-modal")?.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
